import { ChampionshipReview } from '@/features/championship/Tables'
import { ClubAwards } from '@/features/show-release/ClubAwards'
import { ClubStandings } from '@/features/show-release/ClubStandings'
import { useEffect, useState } from 'react'
import { ApiError, type ShowResultsReleasePreviewOutput } from '@schemas'
import { useShowResultsRelease, useShowResultsReleaseMutation } from '@/api/show-release'
import { DataTable, HighlightsReview } from './HighlightsTables'
import { blackJarWinners, showDates } from './public-results'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

type Payload = ShowResultsReleasePreviewOutput['payload']

/**
 * Co-entry figures are keyed by model class. The review names each key by the show classes it
 * covers in this payload, and lists rows in schedule order.
 */
function modelClassLabels(payload: Payload) {
  const labels = new Map<string, string[]>()
  for (const division of payload.divisions)
    for (const cls of division.classes)
      if (cls.modelClassKey)
        labels.set(cls.modelClassKey, [
          ...(labels.get(cls.modelClassKey) ?? []),
          `${cls.code} · ${cls.name}`,
        ])
  const order = new Map([...labels.keys()].map((key, index) => [key, index]))
  const position = (key: string) => order.get(key) ?? order.size
  return {
    label: (key: string) =>
      labels.get(key)?.join(' + ') ?? `Class not listed in this review (key ${key})`,
    compare: (a: string, b: string) => position(a) - position(b) || (a < b ? -1 : a > b ? 1 : 0),
  }
}

/** The released results, laid out for a line-by-line check. Lists everything the pages show. */
function ReviewedResults({ payload }: { payload: Payload }) {
  const modelClasses = modelClassLabels(payload)
  const { awards } = payload
  const winners = [
    ['Best in Show', awards.bestInShow],
    ['Grand Champion', awards.grandChampion],
    ...awards.reserveGrandChampions.map(winner => ['Reserve Grand Champion', winner] as const),
    ...blackJarWinners(payload).map(winner => ['Black Jar', winner] as const),
    ...(awards.bestInDivision ?? []).map(
      item => [`Best in Division: ${item.division.name}`, item.winner] as const
    ),
    ['Hero Award', awards.heroAward],
  ] as const
  const rows = payload.divisions.flatMap(division =>
    division.classes.flatMap(cls => cls.placements.map(placement => ({ division, cls, placement })))
  )
  return (
    <div className="space-y-3">
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-4">
        <dt>Dates</dt>
        <dd>{showDates(payload.show)}</dd>
        <dt>Place</dt>
        <dd>{payload.show.location ?? 'None given'}</dd>
        <dt>Host state</dt>
        <dd>
          {(payload.schemaVersion === 1 ? null : payload.show.hostState) ?? 'Location not set'}
        </dd>
        <dt>Entries</dt>
        <dd>{payload.totals.entries}</dd>
        <dt>Entrants</dt>
        <dd>{payload.totals.entrants}</dd>
        <dt>Clubs</dt>
        <dd>{payload.totals.clubs ?? 'Not tracked'}</dd>
        <dt>Classes with entries</dt>
        <dd>
          {payload.totals.classesWithEntries} of {payload.totals.classesOffered}
        </dd>
      </dl>
      <Table tabIndex={0} className="caption-top">
        <caption className="pb-1 text-left font-medium">Awards to release</caption>
        <TableHeader>
          <TableRow>
            {['Award', 'Exhibit ID', 'Entrant', 'Club', 'Class', 'Entry title'].map(heading => (
              <TableHead scope="col" key={heading}>
                {heading}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {winners.map(([award, winner], index) => (
            <TableRow key={`${award}-${index}`}>
              <TableHead scope="row">{award}</TableHead>
              {winner ? (
                <>
                  <TableCell>{winner.exhibitId ?? '—'}</TableCell>
                  <TableCell>{winner.entrant ?? 'Name withheld'}</TableCell>
                  <TableCell>{winner.club ?? '—'}</TableCell>
                  <TableCell>
                    {winner.class ? `${winner.class.code} · ${winner.class.name}` : '—'}
                  </TableCell>
                  <TableCell>{winner.entryTitle ?? '—'}</TableCell>
                </>
              ) : (
                <TableCell colSpan={5}>Not awarded</TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="max-h-96 overflow-y-auto">
        <Table tabIndex={0} className="caption-top">
          <caption className="pb-1 text-left font-medium">Placings to release</caption>
          <TableHeader>
            <TableRow>
              {['Class', 'Place', 'Exhibit ID', 'Entrant', 'Club', 'Entry title'].map(heading => (
                <TableHead scope="col" key={heading}>
                  {heading}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(({ cls, placement }) => (
              <TableRow key={`${cls.code}-${placement.place}-${placement.exhibitId}`}>
                <TableHead scope="row">
                  {cls.code} · {cls.name}
                </TableHead>
                <TableCell>{placement.place}</TableCell>
                <TableCell>{placement.exhibitId}</TableCell>
                <TableCell>{placement.entrant ?? 'Name withheld'}</TableCell>
                <TableCell>{placement.club ?? '—'}</TableCell>
                <TableCell>{placement.entryTitle ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {!rows.length && (
        <p>No placings are recorded, so only totals, awards and classes would be released.</p>
      )}
      <div className="max-h-96 overflow-y-auto">
        <Table tabIndex={0} className="caption-top">
          <caption className="pb-1 text-left font-medium">Classes to release</caption>
          <TableHeader>
            <TableRow>
              {['Class', 'Division', 'Entries', 'Placings'].map(heading => (
                <TableHead scope="col" key={heading}>
                  {heading}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {payload.divisions.flatMap(division =>
              division.classes.map(cls => (
                <TableRow key={`${division.code}-${cls.code}`}>
                  <TableHead scope="row">
                    {cls.code} · {cls.name}
                  </TableHead>
                  <TableCell>{division.name}</TableCell>
                  <TableCell>{cls.entries}</TableCell>
                  <TableCell>
                    {cls.placements.length || (cls.noAwards ? 'No awards given' : 'None recorded')}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {'coEntries' in payload && (
        <div className="space-y-3">
          <p className="text-sm">
            Class guide co-entry counts to release. Each entrant counts once per model class in this
            show.
          </p>
          <DataTable
            visibleCaption
            caption="Class guide entrants to release"
            headers={['Model class', 'Entrants']}
            rows={[...payload.coEntries.classes]
              .sort((a, b) => modelClasses.compare(a.modelClassKey, b.modelClassKey))
              .map(row => [modelClasses.label(row.modelClassKey), row.entrants])}
          />
          <DataTable
            visibleCaption
            caption="Class guide co-entry pairs to release"
            headers={['Model class', 'Also entered', 'Entrants in both']}
            rows={payload.coEntries.pairs
              // A pair is unordered, so the class scheduled first leads its row.
              .map(row => ({
                classes: [row.classKey, row.alsoClassKey].sort(modelClasses.compare),
                entrants: row.entrants,
              }))
              .sort(
                (a, b) =>
                  modelClasses.compare(a.classes[0], b.classes[0]) ||
                  modelClasses.compare(a.classes[1], b.classes[1])
              )
              .map(row => [...row.classes.map(modelClasses.label), row.entrants])}
          />
        </div>
      )}
      <ClubAwards results={payload} />
      <ClubStandings results={payload} />
      <HighlightsReview payload={payload} />
      {'championship' in payload && payload.championship && (
        <ChampionshipReview data={payload.championship} />
      )}
    </div>
  )
}

export function ReleaseControls({ showId }: { showId: string }) {
  const status = useShowResultsRelease(showId)
  const mutation = useShowResultsReleaseMutation(showId)
  const [preview, setPreview] = useState<ShowResultsReleasePreviewOutput | null>(null)
  const [previewFinalized, setPreviewFinalized] = useState<boolean | null>(null)
  const [withdrawing, setWithdrawing] = useState<number | null>(null)
  const [message, setMessage] = useState('')
  const [expired, setExpired] = useState(false)
  useEffect(() => {
    if (!preview) return
    const remaining = Date.parse(preview.expiresAt) - Date.now()
    if (remaining <= 0) {
      setExpired(true)
      return
    }
    const timer = window.setTimeout(() => setExpired(true), remaining)
    return () => window.clearTimeout(timer)
  }, [preview])
  // A review is only good for the state it was taken in.
  useEffect(() => {
    if (
      status.isError ||
      (preview &&
        status.data &&
        (status.data.generation !== preview.generation ||
          status.data.finalized !== previewFinalized ||
          !status.data.canManage))
    )
      setPreview(null)
  }, [status.isError, status.data, preview, previewFinalized])
  async function act(action: Parameters<typeof mutation.mutateAsync>[0]) {
    setMessage('')
    try {
      const result = await mutation.mutateAsync(action)
      if ('payload' in result) {
        setPreviewFinalized(status.data?.finalized ?? null)
        setPreview(result)
        setExpired(false)
      } else {
        setPreview(null)
        setWithdrawing(null)
        setMessage(result.releaseId ? 'Results released.' : 'Results withdrawn.')
      }
    } catch (error) {
      if (error instanceof ApiError && [400, 401, 403, 404, 409].includes(error.status)) {
        setPreview(null)
        setWithdrawing(null)
        setMessage(
          error.status === 409
            ? 'The results or the release changed. Review again before releasing.'
            : 'This action is unavailable. Reload the release state and check your access.'
        )
      } else
        setMessage(
          'The request could not be confirmed. Check the release state or retry the same action; no success has been assumed.'
        )
    }
  }
  const state = status.data
  if (status.isError)
    return (
      <div role="alert">
        Release state is unavailable.{' '}
        <Button variant="outline" onClick={() => void status.refetch()}>
          Reload release state
        </Button>
      </div>
    )
  if (!state) return <p role="status">Loading release state…</p>
  const busy = mutation.isPending || status.isFetching
  return (
    <section aria-label="Public results release" className="space-y-3 min-w-0">
      <p className="font-medium">
        {state.activeRelease
          ? `Released ${new Date(state.activeRelease.releasedAt).toLocaleString()}`
          : 'Not released'}
      </p>
      {state.differsFromLive && (
        <p role="status" className="text-warning-foreground">
          Results have changed since this release. The public page still shows what was released;
          review and release again to update it.
        </p>
      )}
      <p className="text-sm text-muted-foreground">
        {state.finalized
          ? 'The show is finalized, so its results can be released.'
          : state.canManage
            ? 'Finalize the show before releasing its results. You can review them now.'
            : 'Results can be released once the show is finalized.'}{' '}
        Before release, the public page shows only the show name, dates and place, and only while
        Publish show details is enabled in Setup. No results are public until they are released.
        Reopening the show withdraws the release, and finalizing again does not restore it.
      </p>
      <a className="underline" href={`/public/shows/${encodeURIComponent(state.publicSlug)}`}>
        View the public results page
      </a>
      {state.canManage && (
        <div className="flex flex-wrap gap-2">
          <Button disabled={busy} variant="outline" onClick={() => void act({ kind: 'preview' })}>
            {state.activeRelease ? 'Review results to release again' : 'Review results to release'}
          </Button>
          {state.activeRelease && (
            <Button
              disabled={busy}
              variant="outline"
              onClick={() => setWithdrawing(state.generation)}
            >
              Withdraw
            </Button>
          )}
        </div>
      )}
      {withdrawing !== null && state.canManage && (
        <div role="group" aria-label="Confirm withdrawal" className="space-y-2">
          <p>Take these results off the public page?</p>
          <Button
            disabled={busy}
            onClick={() => void act({ kind: 'withdraw', generation: withdrawing })}
          >
            Confirm withdrawal
          </Button>{' '}
          <Button disabled={busy} variant="outline" onClick={() => setWithdrawing(null)}>
            Cancel
          </Button>
        </div>
      )}
      {preview && state.canManage && (
        <div className="space-y-3" role="region" aria-label="Results review">
          <h4 className="font-semibold">Review: {preview.payload.show.name}</h4>
          <p>
            Everything the public Results and Highlights pages will show is listed here. This review
            expires at {new Date(preview.expiresAt).toLocaleTimeString()}.
          </p>
          <ReviewedResults payload={preview.payload} />
          {preview.uniqueEntrants !== undefined && (
            <p>
              {preview.uniqueEntrants.toLocaleString()} unique people entered. This frozen count
              includes entrants without eligible points; identities remain private.
            </p>
          )}
          {preview.entrantStandings && (
            <section aria-label="Entrant standings to release" className="space-y-3">
              <h5 className="font-semibold">Entrant standings to release</h5>
              <p className="text-sm text-muted-foreground">
                These reviewed points contribute to the championship series leaderboard. Internal
                identities stay private.
              </p>
              <DataTable
                caption="Reviewed entrant points"
                headers={[
                  'Entrant',
                  'Points',
                  'Eligible entries',
                  '1st',
                  '2nd',
                  '3rd',
                  '4th',
                  '5th',
                  '6th',
                ]}
                rows={[...preview.entrantStandings]
                  .sort(
                    (a, b) =>
                      b.points - a.points || (a.entrant ?? '').localeCompare(b.entrant ?? '')
                  )
                  .map(row => [
                    row.entrant ?? 'Name withheld',
                    row.points,
                    row.entries,
                    ...(['1st', '2nd', '3rd', '4th', '5th', '6th'] as const).map(
                      place => row.placements?.[place] ?? 'Unavailable'
                    ),
                  ])}
              />
            </section>
          )}
          {expired && <p role="alert">This review expired. Review the results again.</p>}
          <Button
            disabled={busy || expired || !state.finalized}
            onClick={() => void act({ kind: 'release', token: preview.token })}
          >
            {state.activeRelease ? 'Replace released results' : 'Release reviewed results'}
          </Button>
        </div>
      )}
      {message && <p role="status">{message}</p>}
    </section>
  )
}

/** One line for the show dashboard; the controls themselves live on Reports. */
export function ReleaseStatusNote({ showId }: { showId: string }) {
  const { data } = useShowResultsRelease(showId)
  if (!data) return null
  return (
    <p className="text-sm text-muted-foreground">
      Public results:{' '}
      {data.activeRelease
        ? `released ${new Date(data.activeRelease.releasedAt).toLocaleDateString()}${
            data.differsFromLive ? ', and results have changed since' : ''
          }`
        : 'not released'}
      .
    </p>
  )
}
